/**
 * Price level picker — the pricing hierarchy on the catalogue item page
 * (docs/features/price-precedent/, decisions of 2026-09-15):
 *
 *   0 contracted   an agreed rate for this client and spec — padlocked, dated,
 *                  flagged once older than a year; suggested when it exists
 *   1 last ordered what they paid last time — suggested when it still clears
 *                  the margin floor for this type of work at today's cost
 *   · similar order with no order of this spec at this quantity, an order of the
 *                  same colours and size at up to twice or half the quantity
 *                  or pages, or on another stock, at the margin it was
 *                  charged on today's cost — suggested like last ordered
 *                  within x1.5 on the same stock, otherwise only shown
 *   · last quoted  the same spec quoted and never ordered — a ceiling hint,
 *                  selectable, never suggested
 *   2 policy       the cost-derived price with its levers visible — the level
 *                  a CSR may drop to; the repeat-price guardrail governs the drop
 *   3 custom       anything else, with a recorded reason
 *
 * When the engine recognises no prior order the panel says so and offers
 * search-and-link, so a miss is a visible, fixable thing rather than a
 * silent policy price.
 */
import type { PriceAdjustmentReason } from "@shared-core/data/catalogue-types";
import type {
  LevelVerdict,
  PrecedentCandidate,
  PriceLevel,
  PriceLevelKind,
  PriceLevels,
} from "@shared-core/data/price-precedent-types";
import {
  formatPrecedentDate,
  formatSignedPct,
  formatUnitPence,
} from "@shared-core/domain/price-precedent";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { formatCmPct } from "@shared-core/domain/commercial-exceptions";
import { Lock } from "lucide-react";
import { PrecedentSearchAndLink } from "./PrecedentSearchAndLink";

const LEVEL_LABEL: Record<Exclude<PriceLevelKind, "custom">, string> = {
  contracted: "Contracted rate",
  last_ordered: "Last ordered",
  similar_ordered: "Similar order",
  last_quoted: "Last quoted, not ordered",
  policy: "Policy price",
};

function VerdictChip({
  verdict,
  reason,
}: {
  verdict: LevelVerdict;
  reason: string | null;
}) {
  const tone =
    verdict === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : verdict === "approval_required"
        ? "text-amber-700 dark:text-amber-400"
        : verdict === "blocked"
          ? "text-red-700 dark:text-red-400"
          : "text-muted-foreground";
  const label =
    verdict === "ok"
      ? "within limits"
      : verdict === "approval_required"
        ? "needs approval"
        : verdict === "blocked"
          ? "blocked"
          : "unchecked";
  return (
    <span className={`text-xs ${tone}`} title={reason ?? undefined}>
      {label}
    </span>
  );
}

/** "1000 x 1500mm" for a normalised "1000X1500"; a named size ("A4") as it is. */
function sizeLabel(size: string): string {
  const [width, depth] = size.split("X");
  return depth !== undefined && /^[\d.]+$/.test(width)
    ? `${width} x ${depth}mm`
    : size;
}

/** ", 12 pages" or ", 1000 x 1500mm" when the similar order was another page count or size. Tharstern's page
 *  count (a leaf is two pages, printed one side or both), never printed pages (pp). */
function similarDifference(level: PriceLevel): string {
  const s = level.similar;
  const pages =
    s?.source_pages != null && s.source_pages !== s.pages
      ? `, ${s.source_pages} pages`
      : "";
  const size =
    s?.source_size != null && s.size != null && s.source_size !== s.size
      ? `, ${sizeLabel(s.source_size)}`
      : "";
  return pages + size;
}

/** The margin carried from the similar order, when the viewer may see margins. */
function carriedMargin(level: PriceLevel): string {
  const pct = level.similar?.carried_margin_pct;
  const basis = level.similar?.basis ?? "charged";
  if (basis === "market") {
    return pct != null
      ? `made in house, at its ${formatCmPct(pct)} margin over today's supplier price`
      : "made in house, at its margin over today's supplier price";
  }
  if (basis === "in_house") {
    return pct != null
      ? `made in house, at its ${formatCmPct(pct)} margin over our own cost then`
      : "made in house, at its margin over our own cost then";
  }
  return pct != null
    ? `at its ${formatCmPct(pct)} margin on today's cost`
    : "at the margin it was charged, on today's cost";
}

const rowClass = (selected: boolean) =>
  `flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 ${
    selected
      ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5"
      : "border-border hover:bg-muted"
  }`;

function LevelRow({
  level,
  suggested,
  selected,
  onSelect,
  meta,
}: {
  level: PriceLevel;
  suggested: boolean;
  selected: boolean;
  onSelect: () => void;
  meta: React.ReactNode;
}) {
  const muted = !level.suggestable;
  return (
    <label className={rowClass(selected)}>
      <input
        type="radio"
        name="price-level"
        className="mt-1"
        checked={selected}
        onChange={onSelect}
        aria-label={LEVEL_LABEL[level.kind]}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span
            className={`font-medium ${muted ? "text-muted-foreground" : ""}`}
          >
            {level.kind === "contracted" && (
              <Lock
                className="mr-1.5 inline size-3.5 -translate-y-px"
                aria-hidden
              />
            )}
            {LEVEL_LABEL[level.kind]}
            {suggested && (
              <span className="ml-2 rounded-full bg-[color:var(--color-primary)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-primary)]">
                suggested
              </span>
            )}
          </span>
          <span
            className={`tabular-nums font-semibold ${muted ? "text-muted-foreground" : ""}`}
          >
            {formatQuotedPrice(level.price_pence)}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {formatUnitPence(level.unit_price_pence)}/unit
            </span>
          </span>
        </span>
        <span className="mt-0.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span>{meta}</span>
          <span className="flex items-center gap-2">
            {level.contribution_margin_pct != null && (
              <span>CM {formatCmPct(level.contribution_margin_pct)}</span>
            )}
            <VerdictChip
              verdict={level.verdict}
              reason={level.verdict_reason}
            />
          </span>
        </span>
        {level.contract?.is_stale && (
          <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
            Agreed over a year ago — worth a check before it is relied on again.
          </span>
        )}
        {muted &&
          level.not_suggestable_reason &&
          level.kind !== "last_quoted" && (
            <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
              Not suggested: {level.not_suggestable_reason}
            </span>
          )}
        {level.kind === "policy" && level.levers && (
          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {level.levers.map((l) => (
              <span key={l.label}>
                {l.label} <span className="text-foreground">{l.value}</span>
              </span>
            ))}
          </span>
        )}
      </span>
    </label>
  );
}

export function PriceLevelPicker({
  levels,
  priceGroupName,
  selected,
  customPence,
  customReason,
  manufacturingCostPence = null,
  onSelect,
  onCustomChange,
  onSearchCandidates,
  onLinkPrecedent,
}: {
  levels: PriceLevels;
  priceGroupName: string;
  /** The level in force — the CSR's pick, or the suggestion when they have not picked. */
  selected: PriceLevelKind;
  customPence: number | null;
  customReason: PriceAdjustmentReason | null;
  /** Today's manufacturing cost, when the caller may see it; enables entering
   *  the custom price as a target contribution margin. */
  manufacturingCostPence?: number | null;
  onSelect: (level: PriceLevelKind) => void;
  onCustomChange: (next: {
    pence: number | null;
    reason: PriceAdjustmentReason | null;
  }) => void;
  /** Search-and-link, offered only when the engine recognised nothing. */
  onSearchCandidates?: (query: string) => Promise<PrecedentCandidate[]>;
  onLinkPrecedent?: (candidate: PrecedentCandidate) => void;
}) {
  const byKind = (k: PriceLevel["kind"]) =>
    levels.levels.find((l) => l.kind === k) ?? null;
  const contracted = byKind("contracted");
  const last = byKind("last_ordered");
  const similar = byKind("similar_ordered");
  const lastQuoted = byKind("last_quoted");
  const policy = byKind("policy")!;
  const drop = levels.drop_to_policy;
  const reference = contracted ?? last;
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Price level
        <span className="ml-2 normal-case tracking-normal">
          {levels.work_style} · floor{" "}
          {levels.work_style_floor_pct == null
            ? "none"
            : formatCmPct(levels.work_style_floor_pct)}
        </span>
      </legend>
      {contracted && (
        <LevelRow
          level={contracted}
          suggested={levels.suggested === "contracted"}
          selected={selected === "contracted"}
          onSelect={() => onSelect("contracted")}
          meta={
            contracted.contract
              ? `Agreed ${formatPrecedentDate(contracted.contract.effective_from)} by ${contracted.contract.set_by}${contracted.contract.note ? ` · ${contracted.contract.note}` : ""}`
              : null
          }
        />
      )}
      {last && (
        <LevelRow
          level={last}
          suggested={levels.suggested === "last_ordered"}
          selected={selected === "last_ordered"}
          onSelect={() => onSelect("last_ordered")}
          meta={
            last.source
              ? `${last.source.quantity.toLocaleString()} @ ${formatQuotedPrice(last.source.price_pence)} · ${formatPrecedentDate(last.source.date)} · ${last.source.reference}${last.stock_confirmed === false ? " · stock not confirmed" : ""}`
              : null
          }
        />
      )}
      {similar && (
        <LevelRow
          level={similar}
          suggested={levels.suggested === "similar_ordered"}
          selected={selected === "similar_ordered"}
          onSelect={() => onSelect("similar_ordered")}
          meta={
            similar.source
              ? `${similar.source.quantity.toLocaleString()}${similarDifference(similar)} @ ${formatQuotedPrice(similar.source.price_pence)} · ${formatPrecedentDate(similar.source.date)} · ${similar.source.reference} · ${carriedMargin(similar)}`
              : null
          }
        />
      )}
      {lastQuoted && (
        <LevelRow
          level={lastQuoted}
          suggested={false}
          selected={selected === "last_quoted"}
          onSelect={() => onSelect("last_quoted")}
          meta={
            lastQuoted.source
              ? `${lastQuoted.source.quantity.toLocaleString()} @ ${formatQuotedPrice(lastQuoted.source.price_pence)} · ${formatPrecedentDate(lastQuoted.source.date)} · ${lastQuoted.source.reference} · not ordered`
              : null
          }
        />
      )}
      {levels.unrecognised && onSearchCandidates && onLinkPrecedent && (
        <PrecedentSearchAndLink
          onSearch={onSearchCandidates}
          onLink={onLinkPrecedent}
        />
      )}
      <LevelRow
        level={policy}
        suggested={levels.suggested === "policy"}
        selected={selected === "policy"}
        onSelect={() => onSelect("policy")}
        meta={
          <>
            {priceGroupName}
            {reference && (
              <>
                {" · "}
                <span
                  className={
                    !drop || drop.verdict === "ok"
                      ? ""
                      : drop.verdict === "approval_required"
                        ? "text-amber-700 dark:text-amber-400"
                        : "text-red-700 dark:text-red-400"
                  }
                >
                  {formatSignedPct(
                    reference.price_pence > 0
                      ? ((policy.price_pence - reference.price_pence) /
                          reference.price_pence) *
                          100
                      : 0,
                  )}{" "}
                  vs{" "}
                  {reference.kind === "contracted"
                    ? "contracted"
                    : "last ordered"}
                  {drop?.verdict === "approval_required"
                    ? " · needs approval"
                    : drop?.verdict === "blocked"
                      ? " · blocked by repeat-price limit"
                      : ""}
                </span>
              </>
            )}
          </>
        }
      />
      <label className={rowClass(selected === "custom")}>
        <input
          type="radio"
          name="price-level"
          className="mt-1"
          checked={selected === "custom"}
          onChange={() => onSelect("custom")}
          aria-label="Custom price"
        />
        <span className="min-w-0 flex-1 space-y-2">
          <span className="flex items-center justify-between gap-3">
            <span className="font-medium">Custom</span>
            <span className="flex flex-wrap items-center justify-end gap-2 text-sm">
              <span className="flex items-center gap-1">
                <span className="text-muted-foreground">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  aria-label="Custom price in pounds"
                  className="w-24 rounded-md border border-border bg-background px-2 py-1 text-right tabular-nums"
                  value={
                    customPence == null ? "" : (customPence / 100).toFixed(2)
                  }
                  onFocus={() => onSelect("custom")}
                  onChange={(e) => {
                    const pounds = parseFloat(e.target.value);
                    onCustomChange({
                      pence: Number.isFinite(pounds)
                        ? Math.round(pounds * 100)
                        : null,
                      reason: customReason,
                    });
                  }}
                />
              </span>
              {manufacturingCostPence != null && manufacturingCostPence > 0 && (
                <span className="flex items-center gap-1">
                  <span className="text-muted-foreground">or CM</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="95"
                    aria-label="Custom price as contribution margin percent"
                    className="w-16 rounded-md border border-border bg-background px-2 py-1 text-right tabular-nums"
                    value={
                      customPence == null || customPence <= 0
                        ? ""
                        : (
                            ((customPence - manufacturingCostPence) /
                              customPence) *
                            100
                          ).toFixed(1)
                    }
                    onFocus={() => onSelect("custom")}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value);
                      onCustomChange({
                        pence:
                          Number.isFinite(pct) && pct < 100
                            ? Math.round(
                                manufacturingCostPence / (1 - pct / 100),
                              )
                            : null,
                        reason: customReason,
                      });
                    }}
                  />
                  <span className="text-muted-foreground">%</span>
                </span>
              )}
            </span>
          </span>
          {selected === "custom" && (
            <select
              aria-label="Reason for custom price"
              className="w-full min-w-0 rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={customReason ?? ""}
              onChange={(e) =>
                onCustomChange({
                  pence: customPence,
                  reason: (e.target.value ||
                    null) as PriceAdjustmentReason | null,
                })
              }
            >
              <option value="">Reason for this price…</option>
              <option value="customer_agreement">
                Agreed price with the customer
              </option>
              <option value="commercial_discretion">
                Commercial discretion
              </option>
              <option value="held_at_last_price">
                Holding an earlier price
              </option>
            </select>
          )}
        </span>
      </label>
    </fieldset>
  );
}
